import { useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { FacilitySearchCard } from 'us-react-components/dist/FacilitySearchCards/FacilitySearchCard';
import { FacilityCardSkeleton } from 'us-react-components/dist/FacilitySearchCards/FacilityCardSkeleton';
import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { Facility } from 'us-web-services';
import { useDependencies } from '../../../../../dependencies';
import { useKinsideAuth } from '../../../../../hooks/useKinsideAuth';
import { MoreResultsButton } from '../../../../Button';
import { BestMatchNullState } from '../bestMatch/BestMatchNullState';

type Props = {
  facilities: Facility[];
  hideTitle?: boolean;
  loading?: boolean;
  handleClickViewResults(bookingVerticalTypeId: number | undefined): void;
  handlePostAJob(): void;
}

const SKELETONS = [
  <FacilityCardSkeleton key='facility-1' />,
  <FacilityCardSkeleton key='facility-2' />,
  <FacilityCardSkeleton key='facility-3' />,
  <FacilityCardSkeleton key='facility-4' />,
];

export const DayCaresBestMatch = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { services: { config, tracker }, stores: { user, verification } } = useDependencies();
  const { token, refreshToken, userInfoRef, searchInfoRef, setTokenValues } = useKinsideAuth();

  const {
    facilities,
    handleClickViewResults,
    handlePostAJob,
    hideTitle,
    loading,
  } = props;

  const ssoFormRef = useRef<HTMLFormElement>(null);

  const trackEvent = (event: string, params?: Record<string, unknown>): Promise<void> => tracker.track(event, params);

  const handleFacilitySelect = async (facilityUrl: string): Promise<void> => {
    if (verification.isFailed()) {
      toast.error(t('identityCheck.stripeVerification.verificationFailed.header'));
      return;
    }

    try {
      await user.refreshAuthToken();
      setTokenValues();
      if (ssoFormRef.current) {
        const form = ssoFormRef.current;
        form.action = `${config.KINSIDE_URL}/${facilityUrl}`;
        form.submit();
      }
    } catch {
      user.logout();
    }
  };

  const showFacilities = facilities.slice(0, hideTitle ? 12 : 4);

  return (
    <div className='grid bg-transparent mb-8 min-h-[316px]'>
      {!hideTitle && <div className='block font-bold mb-2.5'>{t('dashboard.widgets.dayCaresBestMatch.label')}</div>}
      {!facilities.length && !loading ? (
        <BestMatchNullState
          header={t('dashboard.widgets.dayCaresBestMatch.nullStateText1')}
          content={t('dashboard.widgets.dayCaresBestMatch.nullStateText2')}
          handlePostAJob={handlePostAJob}
        />
      ) : (
        <>
          <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 mb-8 gap-x-4 gap-y-8'>
            {loading ?
              SKELETONS
            : showFacilities.map(facility => (
              <div
                className='m-b-10'
                key={`${facility.name}-${facility.id}`}>
                  <FacilitySearchCard
                    key={facility.id}
                    facility={facility}
                    trackingCallback={trackEvent}
                    onFacilitySelect={handleFacilitySelect}
                  />
              </div>
            ))}
          </div>
          {!loading && (<div className='flex md:justify-self-end h-fit'>
            <MoreResultsButton
              bookingVerticalTypeId={bookingVerticalType.DAY_CARE}
              handleClickViewResults={handleClickViewResults}
            />
          </div>)}
          <form ref={ssoFormRef} id='kindside-form-daycare-match' className='invisible' method='post' target='_blank'>
            <input id='token' name='token' value={ token } type='hidden' />
            <input id='refreshToken' name='refreshToken' value={ refreshToken } type='hidden' />
            <input ref={userInfoRef} id='userInfo' name='user_info' type='hidden' />
            <input ref={searchInfoRef} id='searchInfo' name='search_info' type='hidden' />
          </form>
        </>
      )}
    </div>
  );
};
